'use client'

import { useEffect, useRef, useCallback } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

// ═══════════════════════════════════════════════════
//  VISITOR ID — Cookie persistant 1 an
//  Identifie le même visiteur même entre sessions
// ═══════════════════════════════════════════════════
function getVisitorId(): string {
  const key = '_sco2_vid'
  // Lire depuis cookie
  const cookies = document.cookie.split('; ')
  const existing = cookies.find((c) => c.startsWith(`${key}=`))
  if (existing) return existing.split('=')[1] ?? ''

  // Créer un nouvel ID persistant
  const vid = `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 6)}`
  // Cookie 365 jours, SameSite=Lax, path=/
  const maxAge = 365 * 24 * 60 * 60
  document.cookie = `${key}=${vid}; path=/; max-age=${maxAge}; SameSite=Lax`
  return vid
}

// ═══════════════════════════════════════════════════
//  SESSION ID — sessionStorage (par onglet)
// ═══════════════════════════════════════════════════
function getSessionId(): string {
  const key = '_sco2_sid'
  let sid = ''
  try { sid = sessionStorage.getItem(key) || '' } catch { /* SSR or blocked */ }
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    try { sessionStorage.setItem(key, sid) } catch { /* noop */ }
  }
  return sid
}

// L'empreinte d'appareil (canvas, WebGL, polices installées, hachée en
// device ID stable) a été retirée entièrement — audit RGPD (24/09/2026),
// constat C2 : cette technique n'a aucune exemption possible au regard de
// l'article 82 de la loi Informatique et Libertés, et aucune base légale ne
// la couvrait ici. Ne pas la réintroduire sans consentement explicite et
// spécifique, distinct du consentement « mesure d'audience ».

// ── UTM params ──
function getUTM(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  }
}

// ── CTA selectors à surveiller ──
const CTA_SELECTORS = [
  'a[href*="/contact"]',
  'a[href*="/demande-devis"]',
  'a[href*="/devis"]',
  'button[type="submit"]',
  '[data-cta]',
  'a[href*="calendly"]',
  'a[href*="tel:"]',
  'a[href*="mailto:"]',
].join(',')

export default function VisitorTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastPath = useRef('')
  const scrollMax = useRef(0)

  // ── Temps réellement passé sur la page ──
  // La durée d'une session n'était jusqu'ici déduite que de l'écart entre deux
  // vues de page, côté serveur. Une visite d'une seule page ne pouvait donc pas
  // avoir de durée — d'où 84 % des sessions à 0 s — et un onglet laissé ouvert
  // produisait des durées de plusieurs jours.
  //
  // On mesure ici le temps pendant lequel la page est réellement visible :
  // le compteur se met en pause quand l'onglet passe en arrière-plan.
  const visibleMs = useRef(0)
  const visibleSince = useRef<number | null>(null)

  const readDwellSeconds = useCallback(() => {
    const pending = visibleSince.current ? Date.now() - visibleSince.current : 0
    return Math.round((visibleMs.current + pending) / 1000)
  }, [])

  const resetDwell = useCallback(() => {
    visibleMs.current = 0
    visibleSince.current = typeof document !== 'undefined' && document.visibilityState === 'visible'
      ? Date.now()
      : null
  }, [])

  // ── Scroll depth tracking (throttled) ──
  const updateScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight)
    const winHeight = window.innerHeight
    const scrollable = docHeight - winHeight
    if (scrollable > 0) {
      const pct = Math.round((scrollTop / scrollable) * 100)
      if (pct > scrollMax.current) scrollMax.current = pct
    }
  }, [])

  // ── CTA click tracking ──
  const handleCtaClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target) return
    const el = target.closest(CTA_SELECTORS)
    if (!el) return

    const label = (el as HTMLElement).innerText?.trim().slice(0, 60)
      || el.getAttribute('aria-label')
      || el.tagName.toLowerCase()
    const href = el.getAttribute('href') || ''

    const data = {
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      type: 'cta_click',
      url: window.location.pathname,
      ctaElement: label,
      ctaHref: href,
    }
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/track', JSON.stringify(data))
    }
  }, [])

  // ── Send scroll + exit data on page leave ──
  const sendExitData = useCallback(() => {
    const data = {
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      type: 'exit',
      url: window.location.pathname,
      scrollDepth: scrollMax.current,
      dwellTime: readDwellSeconds(),
    }
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/track', JSON.stringify(data))
    }
  }, [readDwellSeconds])

  // ── Global listeners ──
  useEffect(() => {
    let scrollTimer: ReturnType<typeof setTimeout> | null = null
    const throttledScroll = () => {
      if (scrollTimer) return
      scrollTimer = setTimeout(() => { updateScroll(); scrollTimer = null }, 500)
    }

    // `beforeunload` ne part pas de façon fiable — les navigateurs mobiles le
    // sautent quand l'utilisateur change d'application ou ferme l'onglet.
    // `visibilitychange` est l'évènement recommandé : il se déclenche aussi
    // bien au passage en arrière-plan qu'à la fermeture. `pagehide` couvre la
    // navigation arrière depuis le cache de page.
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (visibleSince.current) {
          visibleMs.current += Date.now() - visibleSince.current
          visibleSince.current = null
        }
        sendExitData()
      } else if (!visibleSince.current) {
        visibleSince.current = Date.now()
      }
    }

    if (document.visibilityState === 'visible' && !visibleSince.current) {
      visibleSince.current = Date.now()
    }

    window.addEventListener('scroll', throttledScroll, { passive: true })
    document.addEventListener('click', handleCtaClick, true)
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', sendExitData)

    return () => {
      window.removeEventListener('scroll', throttledScroll)
      document.removeEventListener('click', handleCtaClick, true)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', sendExitData)
      if (scrollTimer) clearTimeout(scrollTimer)
    }
  }, [updateScroll, handleCtaClick, sendExitData])

  // ── Page view tracking ──
  useEffect(() => {
    const fullPath = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')
    if (fullPath === lastPath.current) return

    // Clore la page précédente : scroll atteint et temps réellement passé.
    // On envoie même si le scroll est nul, sinon le temps passé sur une page
    // courte — qui tient dans l'écran — ne serait jamais remonté.
    if (lastPath.current) {
      const dwell = readDwellSeconds()
      if (scrollMax.current > 0 || dwell > 0) {
        const scrollData = {
          sessionId: getSessionId(),
          visitorId: getVisitorId(),
          type: 'scroll',
          url: lastPath.current,
          scrollDepth: scrollMax.current,
          dwellTime: dwell,
        }
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/track', JSON.stringify(scrollData))
        }
      }
    }

    // Reset scroll et compteur de temps pour la nouvelle page
    scrollMax.current = 0
    resetDwell()
    lastPath.current = fullPath

    // Petit délai pour ne pas impacter le LCP
    const timer = setTimeout(() => {
      const sessionId = getSessionId()
      const visitorId = getVisitorId()
      const utm = getUTM()

      const data = {
        sessionId,
        visitorId,
        type: 'pageview',
        url: pathname,
        title: document.title,
        referrer: document.referrer,
        screenSize: `${screen.width}x${screen.height}`,
        language: navigator.language || '',
        ...utm,
      }

      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/track', JSON.stringify(data))
      } else {
        fetch('/api/track', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => {})
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [pathname, searchParams, readDwellSeconds, resetDwell])

  return null // Composant invisible
}
